import { useState, useEffect, useRef } from 'react'
import { Crown, Loader2, Plus, X, HelpCircle } from '@/ui/icons'
import { SectionCard } from './SectionCard'
import { Button } from '@/ui/design-system'
import { ConfirmationDialog } from '@/ui/components'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/ui/components/primitives/tooltip'
import { ANNOUNCEMENTS_TOOLTIP_COPY } from '@/shared/copy/tooltips'
import { useTripAnnouncements } from '@/modules/announcements'
import { useTripDetailPanel } from '../contexts/TripDetailPanelContext'
import { useConfirm } from '@/shared/hooks'
import { canEditTrip } from '@/shared/utils/auth'
import { cn } from '@/shared/utils'
import { Textarea } from '@/ui/components'
import { getUserProfileUrl } from '@/shared/utils/userImages'
import type {
  TripAnnouncement,
  TripResponse,
  MemberResponse,
} from '@/shared/api/types'

const COLLAPSED_COUNT = 4

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / (1000 * 60))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins}m ago`
  if (diffHours < 24) return `${diffHours}h ago`
  if (diffDays < 7) return `${diffDays}d ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  return date.toLocaleDateString()
}

function getAuthorDisplay(
  authorId: string | null,
  trip: TripResponse & { members?: MemberResponse[] },
  members: MemberResponse[]
): { name: string; initials: string; isOwner: boolean; avatarUrl: string | null } {
  if (!authorId)
    return { name: 'Unknown', initials: '?', isOwner: false, avatarUrl: null }

  const createdBy = trip.created_by
  const member = members.find((m) => m.user_id === authorId)
  const isOwner =
    authorId === createdBy || member?.role === 'owner'

  if (member) {
    const name =
      member.guest_name ??
      member.user?.user_metadata?.full_name ??
      member.user?.email ??
      'Crew member'
    const parts = String(name).trim().split(/\s+/)
    const initials =
      (parts[0]?.charAt(0) ?? '') + (parts[1]?.charAt(0) ?? '').toUpperCase() || '?'
    const avatarUrl =
      member.member_type === 'full_member' && member.user_profile?.profile_image_path
        ? getUserProfileUrl(member.user_profile.profile_image_path)
        : null
    return { name, initials, isOwner, avatarUrl }
  }

  if (authorId === createdBy) {
    return { name: 'Trip creator', initials: 'TC', isOwner: true, avatarUrl: null }
  }

  return { name: 'Unknown', initials: '?', isOwner: false, avatarUrl: null }
}

/** Compose form for posting an announcement; can be rendered in header or inline. */
export interface AnnouncementComposeFormProps {
  onCreateAnnouncement: (dto: { content: string }) => Promise<unknown>
  onSuccess?: () => void
  placeholder?: string
  className?: string
}

export function AnnouncementComposeForm({
  onCreateAnnouncement,
  onSuccess,
  placeholder = 'Post an announcement for the crew...',
  className,
}: AnnouncementComposeFormProps) {
  const [content, setContent] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async () => {
    const trimmed = content.trim()
    if (!trimmed) return
    setIsSubmitting(true)
    try {
      await onCreateAnnouncement({ content: trimmed })
      setContent('')
      onSuccess?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div
      className={cn(
        'rounded-md border border-earth-200 dark:border-earth-700 bg-earth-50/50 dark:bg-earth-900/20 p-3',
        className
      )}
    >
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className="w-full resize-none text-sm"
        disabled={isSubmitting}
      />
      <div className="mt-2 flex justify-end">
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!content.trim() || isSubmitting}
        >
          {isSubmitting ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          ) : (
            'Post'
          )}
        </Button>
      </div>
    </div>
  )
}

interface AnnouncementsSectionProps {
  tripId: string
  trip: TripResponse & { members?: MemberResponse[] }
  user: { id: string } | null
}

export function AnnouncementsSection({ tripId, trip, user }: AnnouncementsSectionProps) {
  const { setSelectedDetail, selectedDetail } = useTripDetailPanel()
  const {
    announcements,
    isLoading,
    error,
    refetch,
    deleteAnnouncement,
  } = useTripAnnouncements(tripId)

  const { confirm, confirmState, close, setLoading: setConfirmLoading } = useConfirm()
  const [expanded, setExpanded] = useState(false)
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null)
  const wasAnnouncementAddRef = useRef(false)

  useEffect(() => {
    if (selectedDetail?.type === 'announcement-add') {
      wasAnnouncementAddRef.current = true
      return
    }
    if (wasAnnouncementAddRef.current) {
      refetch()
      wasAnnouncementAddRef.current = false
    }
  }, [selectedDetail?.type, refetch])

  const members = trip.members ?? []
  const canPost = user && canEditTrip(trip, user)

  const displayList = expanded
    ? announcements
    : announcements.slice(0, COLLAPSED_COUNT)
  const hasMore = announcements.length > COLLAPSED_COUNT
  const showExpandToggle = hasMore && !expanded
  const showCollapseToggle = hasMore && expanded

  const handleDelete = (id: string) => {
    confirm(
      {
        title: 'Delete announcement',
        description: 'Are you sure you want to remove this announcement?',
        variant: 'destructive',
        confirmText: 'Delete',
      },
      async () => {
        setConfirmLoading(true)
        setIsDeletingId(id)
        try {
          await deleteAnnouncement(id)
        } finally {
          setIsDeletingId(null)
          setConfirmLoading(false)
        }
      }
    )
  }

  const sectionTitle = (
    <>
      <span>Announcements</span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-full p-0.5 text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="What are announcements?"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" align="start" className="max-w-xs">
          <p className="text-sm text-muted-foreground leading-relaxed">{ANNOUNCEMENTS_TOOLTIP_COPY}</p>
        </TooltipContent>
      </Tooltip>
    </>
  )

  if (error) {
    return (
      <section id="announcements-section" className="min-w-0 flex-1 min-h-0 flex flex-col overflow-hidden">
        <SectionCard title={sectionTitle} count={0} fixedHeight className="min-w-0 flex-1 min-h-0">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-2">
            Try again
          </Button>
        </SectionCard>
      </section>
    )
  }

  const addButton = canPost && (
    <Button
      variant="outline"
      size="sm"
      onClick={() => setSelectedDetail({ type: 'announcement-add' })}
      className="gap-2"
    >
      <Plus className="h-4 w-4" />
      Add
    </Button>
  )

  return (
    <section id="announcements-section" className="min-w-0 flex-1 min-h-0 flex flex-col overflow-hidden">
      <SectionCard
        title={sectionTitle}
        count={announcements.length}
        actions={addButton}
        className="min-w-0 flex-1 min-h-0"
        contentClassName="pt-2"
        fixedHeight
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-earth-500 dark:text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
          </div>
        ) : (
          <>
            {announcements.length === 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-earth-500 dark:text-muted-foreground py-1.5">
                  No announcements yet.
                </p>
              </div>
            ) : (
              <ul className="space-y-2 min-w-0">
                {displayList.map((announcement) => (
                  <AnnouncementCard
                    key={announcement.id}
                    announcement={announcement}
                    trip={trip}
                    members={members}
                    canDelete={Boolean(canPost)}
                    isDeleting={isDeletingId === announcement.id}
                    onDelete={() => handleDelete(announcement.id)}
                  />
                ))}
              </ul>
            )}

            {showExpandToggle && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="mt-2 text-xs text-adventure-600 hover:text-adventure-700 dark:text-foreground dark:hover:text-muted-foreground"
              >
                Show more
              </button>
            )}
            {showCollapseToggle && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="mt-2 text-xs text-adventure-600 hover:text-adventure-700 dark:text-foreground dark:hover:text-muted-foreground"
              >
                Show less
              </button>
            )}
          </>
        )}
      </SectionCard>
      <ConfirmationDialog
        open={confirmState?.open ?? false}
        onOpenChange={(open) => !open && close()}
        title={confirmState?.title}
        description={confirmState?.description ?? ''}
        confirmText={confirmState?.confirmText}
        cancelText={confirmState?.cancelText}
        variant={confirmState?.variant}
        onConfirm={confirmState?.onConfirm ?? (() => {})}
        isLoading={confirmState?.isLoading}
      />
    </section>
  )
}

interface AnnouncementCardProps {
  announcement: TripAnnouncement
  trip: TripResponse & { members?: MemberResponse[] }
  members: MemberResponse[]
  canDelete: boolean
  isDeleting: boolean
  onDelete: () => void
}

const URL_PATTERN = /((https?:\/\/|www\.)[^\s]+)/gi

function renderContentWithLinks(content: string) {
  const parts: Array<{ text: string; isLink: boolean }> = []
  let lastIndex = 0

  content.replace(URL_PATTERN, (match, _group, _protocol, offset) => {
    if (typeof offset === 'number' && offset > lastIndex) {
      parts.push({ text: content.slice(lastIndex, offset), isLink: false })
    }
    parts.push({ text: match, isLink: true })
    lastIndex = (offset as number) + match.length
    return match
  })

  if (lastIndex < content.length) {
    parts.push({ text: content.slice(lastIndex), isLink: false })
  }

  if (parts.length === 0) {
    return content
  }

  return parts.map((part, index) =>
    part.isLink ? (
      <a
        key={index}
        href={part.text.startsWith('http') ? part.text : `https://${part.text}`}
        target="_blank"
        rel="noopener noreferrer"
        className="underline text-adventure-700 dark:text-adventure-300 break-words"
      >
        {part.text}
      </a>
    ) : (
      <span key={index}>{part.text}</span>
    )
  )
}

function AnnouncementCard({
  announcement,
  trip,
  members,
  canDelete,
  isDeleting,
  onDelete,
}: AnnouncementCardProps) {
  const { name, initials, isOwner, avatarUrl } = getAuthorDisplay(
    announcement.author_id,
    trip,
    members
  )

  return (
    <li
      className={cn(
        'rounded-md border border-earth-200 dark:border-earth-700 bg-earth-50/50 dark:bg-earth-900/20 p-3 min-w-0',
        'hover:border-adventure-200 dark:hover:border-adventure-800 transition-colors'
      )}
    >
      <div className="flex flex-col gap-1.5 @sm:flex-row @sm:items-start @sm:justify-between">
        <div className="flex min-w-0 flex-1 gap-2">
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              className="h-8 w-8 flex-shrink-0 rounded-full object-cover border border-earth-200 dark:border-earth-600"
              aria-hidden
            />
          ) : (
            <div
              className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-adventure-100 text-adventure-700 dark:bg-adventure-900/50 dark:text-adventure-300 text-xs font-medium"
              aria-hidden
            >
              {initials}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1">
              <span className="font-medium text-sm text-earth-900 dark:text-foreground">
                {name}
              </span>
              {isOwner && (
                <Crown
                  className="h-3.5 w-3.5 text-adventure-600 dark:text-adventure-400 flex-shrink-0"
                  aria-label="Trip owner"
                />
              )}
            </div>
            <p className="mt-0.5 whitespace-pre-wrap text-xs text-earth-700 dark:text-foreground">
              {renderContentWithLinks(announcement.content)}
            </p>
          </div>
        </div>
        <div className="flex flex-shrink-0 w-full @sm:w-auto items-center gap-3 @sm:flex-col @sm:items-end">
          <span className="text-xs text-earth-500 dark:text-muted-foreground">
            {formatRelativeTime(announcement.created_at)}
          </span>
          {canDelete && (
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 ml-auto @sm:ml-0 text-earth-500 hover:text-destructive hover:bg-destructive/10 dark:text-earth-400 dark:hover:text-destructive"
              onClick={onDelete}
              disabled={isDeleting}
              aria-label="Remove post"
            >
              {isDeleting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <X className="h-3.5 w-3.5" aria-hidden />
              )}
            </Button>
          )}
        </div>
      </div>
    </li>
  )
}
